import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ExternalLink } from 'lucide-react';
import InterviewMedia from '../InterviewMedia';

interface ContentLayoutProps {
  isReversed: boolean;
  context: string;
  contextUrl?: string;
  contextImageUrl?: string;
  contextImageCaption?: string;
  question: string;
  answer: string;
  intervieweeName: string;
  title: string;
  jobTitle?: string;
  company: string;
  tags: string[];
  isExpanded: boolean;
  photoUrl?: string;
  interviewId?: string; // Add interview ID
}

const ContentLayout = ({
  isReversed,
  context,
  contextUrl,
  contextImageUrl,
  contextImageCaption,
  question,
  answer,
  intervieweeName,
  title,
  jobTitle,
  company,
  tags,
  isExpanded,
  photoUrl,
  interviewId
}: ContentLayoutProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Parse context for NTB data if it's available
  const ntbData = React.useMemo(() => {
    if (!context || typeof context !== 'string') return null;
    
    try {
      const parsed = JSON.parse(context);
      // Check if it's NTB data (has id and ntbId or title)
      if (parsed && (parsed.id || parsed.ntbId || parsed.title)) {
        return parsed;
      }
    } catch (e) {
      // Not JSON, ignore
    }
    return null;
  }, [context]);

  // Get image data - prioritize contextImageUrl/Caption, fallback to parsed context
  const imageData = React.useMemo(() => {
    // First, check if we have direct context image data
    if (contextImageUrl) {
      return {
        imageUrl: contextImageUrl,
        imageCaption: contextImageCaption || ''
      };
    }

    // Fallback: try to extract from ntbData/context
    if (ntbData) {
      const directUrl = ntbData.imageUrl || '';
      const directCaption = ntbData.imageCaption || '';
      if (directUrl) {
        return { imageUrl: directUrl, imageCaption: directCaption };
      }

      // Fallback to mediaList if available
      const mediaList = ntbData.mediaList || ntbData.media || ntbData.media_list;
      if (Array.isArray(mediaList) && mediaList.length) {
        const preferred = mediaList.find((m: any) => 
          /(image|photo|picture)/i.test(m?.type || m?.contentType || m?.mediaType || '')
        ) || mediaList[0];
        const url = preferred?.url || preferred?.href || preferred?.mediaUrl || preferred?.imageUrl || '';
        const caption = preferred?.caption || preferred?.description || preferred?.credit || ntbData?.title || '';
        if (url) {
          return { imageUrl: url, imageCaption: caption };
        }
      }
    }

    return null;
  }, [contextImageUrl, contextImageCaption, ntbData]);

  // --- Hjelpefunksjoner ---
  const convertToMarkdown = (text: string) => text.replace(/^•\s*/gm, '- ');
  const isNTBArticle = (url?: string) => url?.startsWith('ntb:');

  const getNTBTitle = (context: string) => {
    try {
      const ntbData = JSON.parse(context);
      return ntbData?.title || 'NTB Artikkel';
    } catch {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = context;
      return tempDiv.querySelector('.lead')?.textContent?.trim() || tempDiv.textContent?.slice(0, 100) + '...' || 'NTB Artikkel';
    }
  };

  // --- RENDER-FUNKSJONER ---
  const renderContext = () => {
    if (!context || !isNTBArticle(contextUrl)) return null;
    
    return (
      <div className="mb-4">
        <span 
          className="text-base font-medium text-foreground cursor-pointer hover:text-primary transition-colors inline"
          onClick={() => setIsDialogOpen(true)}
        >
          {getNTBTitle(context)} <ExternalLink className="h-4 w-4 inline ml-1" />
        </span>
      </div>
    );
  };

  const NTBArticleDialog = () => {
    if (!isNTBArticle(contextUrl)) return null;

    let ntbData;
    try {
      // VIKTIG: Parser 'context'-strengen til et JSON-objekt
      ntbData = JSON.parse(context);
    } catch (e) {
      // Hvis parsing feiler, er det sannsynligvis ren HTML (legacy-format)
      return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="w-full max-w-4xl h-[80vh]">
            <div className="w-full h-full overflow-auto p-6 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: context }} />
          </DialogContent>
        </Dialog>
      );
    }

    // Hvis parsing er vellykket, render den nye, robuste komponenten
    // Debug: Log available date fields
    React.useEffect(() => {
      if (ntbData) {
        console.log('NTB Data fields:', Object.keys(ntbData));
        console.log('Available date fields:', {
          publishedDate: ntbData.publishedDate,
          published: ntbData.published,
          date: ntbData.date,
          timestamp: ntbData.timestamp
        });
      }
    }, [ntbData]);

    return (
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-full max-w-4xl h-[80vh]">
          <div className="w-full h-full overflow-auto p-6 space-y-4">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{ntbData.title}</DialogTitle>
            </DialogHeader>
            
            {/* Display publication date and time from NTB API */}
            {ntbData.publishedDate && (
              <p className="text-sm text-muted-foreground">
                Publisert: {new Date(ntbData.publishedDate).toLocaleDateString('nb-NO', {
                  day: '2-digit',
                  month: '2-digit', 
                  year: 'numeric'
                })}, kl. {new Date(ntbData.publishedDate).toLocaleTimeString('nb-NO', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
            
            {/* BRUK DE SEPARATE BILDE-FELTENE FØRST, FALL TILBAKE PÅ IMAGEDATA */}
            {imageData && (
              <div className="mb-4">
                <figure className="my-6">
                  <img 
                    src={imageData.imageUrl} 
                    alt={imageData.imageCaption || ntbData.title}
                    className="w-full h-auto rounded-md mx-auto block max-w-lg"
                  />
                  {imageData.imageCaption && (
                    <figcaption className="text-sm text-muted-foreground italic mt-2 text-center">
                      {imageData.imageCaption}
                    </figcaption>
                  )}
                </figure>
              </div>
            )}

            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ 
                __html: (ntbData.content || '').replace(/<img[^>]*>/g, '').replace(/<figure[^>]*>.*?<\/figure>/gs, '') 
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className={cn(
      "flex flex-col md:flex-row gap-4 md:gap-6 items-center",
      isReversed ? "md:flex-row-reverse" : ""
    )}>
      <div className="flex justify-center overflow-visible">
        <InterviewMedia 
          intervieweeName={intervieweeName} 
          photoUrl={photoUrl} 
          interviewId={interviewId}
        />
      </div>
      
      <div className="w-full md:w-3/4">  {/* Økt fra md:w-1/2 til md:w-3/4 */}
        {renderContext()}
        
        <Link to={`/question/${encodeURIComponent(question)}`}>
          <h2 className="text-lg font-semibold cursor-pointer hover:text-primary transition-colors" style={{ color: '#ea384c' }}>{question}</h2>
        </Link>
        
        <div className="mt-2 mb-3">
          <span 
            className="font-medium text-foreground block cursor-pointer transition-colors hover:text-primary"
            onClick={() => window.location.href = `/person/${encodeURIComponent(intervieweeName)}`}
          >
            {intervieweeName}
          </span>
          {(jobTitle || company) && (
            <div className="text-sm text-muted-foreground">
              {company ? (
                <Link 
                  to={`/company/${encodeURIComponent(company)}`}
                  className="cursor-pointer transition-colors hover:text-primary"
                >
                  {jobTitle ? `${jobTitle} · ${company}` : company}
                </Link>
              ) : jobTitle ? (
                <span>{jobTitle}</span>
              ) : null}
            </div>
          )}
        </div>
        
        <div className={cn(
          "my-4 relative",
          !isExpanded && "overflow-hidden after:absolute after:bottom-0 after:left-0 after:right-0 after:h-20 after:bg-gradient-to-t after:from-white after:to-transparent after:pointer-events-none"
        )}
        style={{ maxHeight: !isExpanded ? '160px' : 'none' }}
        >
          <div className="">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({node, ...props}) => (
                  <h1 className="!text-base !my-3 !font-medium" style={{ fontWeight: 500 }} {...props} />
                ),
                h2: ({node, ...props}) => (
                  <h2 className="!text-base !my-2 !font-medium" style={{ fontWeight: 500 }} {...props} />
                ),
                h3: ({node, ...props}) => (
                  <h3 className="!text-base !my-2 !font-medium" style={{ fontWeight: 500 }} {...props} />
                ),
                p: ({node, ...props}) => (
                  <p className="mb-3 leading-relaxed" {...props} />
                ),
                ul: ({node, ...props}) => (
                  <ul className="list-disc list-inside pl-4 my-2" {...props} />
                )
              }}
            >
              {answer}
            </ReactMarkdown>
          </div>
        </div>
        
        {/* Hashtags after answer */}
        {tags && tags.length > 0 && (
          <div className="mt-3 mb-3">
            {tags
              .filter(tag => typeof tag === 'string' && tag.trim() !== '' && tag !== '{}' && tag !== '"#{}"')
              .map((tag, index) => {
                // Clean the tag thoroughly - enhanced cleaning
                let cleanTag = String(tag)
                  .replace(/^["']|["']$/g, '') // Remove surrounding quotes
                  .replace(/["']/g, '') // Remove ANY quotes within the string
                  .replace(/^#+/, '') // Remove any leading hashtags
                  .replace(/[{}]/g, '') // Remove curly braces
                  .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                  .replace(/[^\w\sæøåÆØÅ-]/g, '') // Remove special characters except Norwegian letters and hyphens
                  .trim();
                
                // Skip empty tags after cleaning
                if (!cleanTag) return null;
                
                return (
                  <Badge key={`${cleanTag}-${index}`} className="mr-2 bg-muted text-muted-foreground hover:bg-muted/80 border-transparent">
                    <Link to={`/tag/${encodeURIComponent(cleanTag)}`}>
                      #{cleanTag}
                    </Link>
                  </Badge>
                );
              })
              .filter(Boolean)} {/* Remove null entries */}
          </div>
        )}
      </div>
      
      <NTBArticleDialog />
    </div>
  );
};

export default ContentLayout;